import React from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { cn } from '@utils/cn';

export interface CollapsibleGroupHeaderProps {
  /** Group label. Truncates rather than wrapping so the count never moves. */
  title: string;
  /** Rows inside the group *as currently filtered* — the one thing a folded group still says. */
  count: number;
  expanded: boolean;
  /** Omit to render static: no chevron, not a button, not focusable. */
  onToggle?: () => void;
  /** Id of the element holding the group's rows — wires `aria-controls`. */
  controlsId?: string;
  className?: string;
}

/**
 * A one-line, sticky header for a group inside a scrolling list.
 *
 * Deliberately flat: no card shell and no `overflow-hidden`. Either one would
 * become the header's nearest clipping ancestor and quietly kill the `sticky`
 * positioning it relies on — it must stick to the scroller that owns it, not to
 * its own group box. For the same reason it renders exactly one element and
 * never wraps itself.
 *
 * The count stays visible while expanded too: the layout then doesn't shift on
 * toggle, and it is the only feedback a folded group can give when a row lands
 * inside it.
 */
export const CollapsibleGroupHeader: React.FC<CollapsibleGroupHeaderProps> = ({
  title,
  count,
  expanded,
  onToggle,
  controlsId,
  className,
}) => {
  const isInteractive = typeof onToggle === 'function';

  const baseClasses = cn(
    'w-full flex items-center gap-1.5 text-left',
    'sticky top-0 z-10 bg-white/95 dark:bg-gray-800/95 backdrop-blur',
    'px-3 py-1 border-b border-gray-100 dark:border-gray-700',
    'text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500',
    isInteractive &&
      'transition-colors hover:bg-gray-50/95 dark:hover:bg-gray-700/95',
    className,
  );

  const body = (
    <>
      {isInteractive && (
        <KeyboardArrowDownIcon
          sx={{ fontSize: 14 }}
          className={cn(
            'shrink-0 transition-transform duration-200',
            expanded ? 'rotate-0' : '-rotate-90',
          )}
        />
      )}
      <span className="truncate">{title}</span>
      <span className="ml-auto shrink-0 tabular-nums">{count}</span>
    </>
  );

  if (!isInteractive) {
    return <div className={baseClasses}>{body}</div>;
  }

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-controls={controlsId}
      className={baseClasses}>
      {body}
    </button>
  );
};

export default CollapsibleGroupHeader;
